import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import TierApprovalDialog from "../components/TierApprovalDialog";
import { Save, Scroll, ShieldAlert, BookOpen } from "lucide-react";

export default function Editors() {
  const [cfg, setCfg] = useState(null);
  const [dirty, setDirty] = useState({ hard_limits: null, voice: null, behaviour: null });
  const [dialog, setDialog] = useState({ open: false, tier: "A", target: null });

  const load = async () => {
    const { data } = await api.get("/config");
    setCfg(data);
  };
  useEffect(() => { load(); }, []);

  if (!cfg) return <div className="font-mono-elle text-sm" style={{ color: "var(--elle-ink-dim)" }}>Loading…</div>;

  const openSave = (target) => {
    const tier = target === "hard_limits" ? "B" : "A";
    setDialog({ open: true, tier, target });
  };

  const confirmSave = async (passphrase, totp) => {
    const target = dialog.target;
    const payload = dirty[target] ?? cfg[target];
    const body = { data: payload, passphrase };
    if (dialog.tier === "B") body.totp_code = totp;
    const endpoint = `/config/${target.replace("_", "-")}`;
    await api.put(endpoint, body);
    toast.success("Saved.");
    setDirty((d) => ({ ...d, [target]: null }));
    await load();
  };

  const jsonArea = (target, testid) => (
    <Textarea data-testid={testid} rows={20}
              className="font-mono-elle text-xs"
              value={JSON.stringify(dirty[target] ?? cfg[target] ?? {}, null, 2)}
              onChange={(e) => {
                try {
                  const parsed = JSON.parse(e.target.value);
                  setDirty((d) => ({ ...d, [target]: parsed }));
                } catch (_err) {
                  // allow invalid while typing; guard on save
                  setDirty((d) => ({ ...d, [target]: e.target.value }));
                }
              }}/>
  );

  return (
    <div data-testid="editors-page">
      <div className="mb-8">
        <div className="overline">Editors</div>
        <h1 className="font-display text-4xl mt-2">The rulebook.</h1>
        <p className="mt-2 text-sm max-w-2xl" style={{ color: "var(--elle-ink-dim)" }}>
          Three books on a shelf. Voice and Behaviour bend with a passphrase.
          Hard Limits need the passphrase AND the 2FA code — not a line is changed otherwise.
        </p>
      </div>

      <Tabs defaultValue="voice" className="space-y-6">
        <TabsList className="bg-transparent border rounded-sm" style={{ borderColor: "var(--elle-border)" }}>
          <TabsTrigger value="voice" data-testid="tab-voice" className="rounded-sm">
            <BookOpen size={14} className="mr-2"/> Voice & Works
          </TabsTrigger>
          <TabsTrigger value="behaviour" data-testid="tab-behaviour" className="rounded-sm">
            <Scroll size={14} className="mr-2"/> Behaviour rules
          </TabsTrigger>
          <TabsTrigger value="limits" data-testid="tab-limits" className="rounded-sm">
            <ShieldAlert size={14} className="mr-2"/> Hard Limits
          </TabsTrigger>
        </TabsList>

        <TabsContent value="voice">
          <div className="elle-surface p-6 rounded-sm">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="overline tier-a">Tier A — passphrase required</div>
                <h3 className="font-display text-2xl mt-1">Voice profile</h3>
              </div>
              <Button data-testid="save-voice" onClick={()=>openSave("voice")}
                      disabled={dirty.voice == null || typeof dirty.voice === "string"}
                      className="btn-amber rounded-sm">
                <Save size={14} className="mr-2"/> Save (Tier A)
              </Button>
            </div>
            {jsonArea("voice", "voice-json")}
          </div>
        </TabsContent>

        <TabsContent value="behaviour">
          <div className="elle-surface p-6 rounded-sm">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="overline tier-a">Tier A — passphrase required</div>
                <h3 className="font-display text-2xl mt-1">Behaviour rules</h3>
              </div>
              <Button data-testid="save-behaviour" onClick={()=>openSave("behaviour")}
                      disabled={dirty.behaviour == null || typeof dirty.behaviour === "string"}
                      className="btn-amber rounded-sm">
                <Save size={14} className="mr-2"/> Save (Tier A)
              </Button>
            </div>
            {jsonArea("behaviour", "behaviour-json")}
          </div>
        </TabsContent>

        <TabsContent value="limits">
          <div className="elle-surface p-6 rounded-sm border"
               style={{ borderColor: "var(--elle-oxblood)" }}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="overline tier-b">Tier B — passphrase + 2FA required</div>
                <h3 className="font-display text-2xl mt-1">Hard Limits</h3>
              </div>
              <Button data-testid="save-limits" onClick={()=>openSave("hard_limits")}
                      disabled={dirty.hard_limits == null || typeof dirty.hard_limits === "string"}
                      className="btn-oxblood rounded-sm">
                <Save size={14} className="mr-2"/> Save (Tier B)
              </Button>
            </div>
            {jsonArea("hard_limits", "limits-json")}
          </div>
        </TabsContent>
      </Tabs>

      <TierApprovalDialog
        open={dialog.open}
        onOpenChange={(v) => setDialog((d) => ({ ...d, open: v }))}
        tier={dialog.tier}
        title={`Save ${dialog.target?.replace("_", " ")}`}
        description="Provide your credentials to commit this change. The decision log will record it."
        onConfirm={confirmSave}
      />
    </div>
  );
}
